import React, { Fragment, useState, useMemo } from 'react';
import Link from 'shared/SlashLink';
import limits from '../../data/ca_income_rent_limits.json';
import usePageSeo from '../../hooks/usePageSeo';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';

const HOUSEHOLD = ['1 Person', '2 People', '3 People', '4 People', '5 People', '6 People', '7 People', '8 People'];
const BEDROOMS = ['Studio', '1 Bedroom', '2 Bedroom', '3 Bedroom', '4 Bedroom', '5 Bedroom'];

// Modernized native <select> — long option lists (58 counties, 11 AMI levels)
// stay a native select for usability, just restyled to match the brand.
const AMI_BLUE = '#0044EB', AMI_INK = '#16203C', AMI_LINE = '#D7E2F1';
const amiCss = `
.ami__select{appearance:none;-webkit-appearance:none;-moz-appearance:none;width:100%;cursor:pointer;
  font-family:'Axiforma Regular',sans-serif;font-size:15px;color:${AMI_INK};background-color:#F7FAFD;
  border:1.5px solid ${AMI_LINE};border-radius:12px;padding:12px 40px 12px 14px;
  background-image:url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2354648A' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat:no-repeat;background-position:right 14px center;background-size:16px;
  transition:border-color .15s,box-shadow .15s,background-color .15s;}
.ami__select:hover{border-color:#B9CBEA}
.ami__select:focus{outline:none;border-color:${AMI_BLUE};background-color:#fff;box-shadow:0 0 0 4px rgba(0,68,235,.10)}
`;

const COUNTIES = Object.entries(limits)
    .map(([slug, d]) => ({ slug, name: d.name }))
    .sort((a, b) => a.name.localeCompare(b.name));

const fmt = (n) => (n == null ? '—' : '$' + n.toLocaleString());

const AmiLimitsPage = () => {
    const [county, setCounty] = useState('los-angeles');
    const [ami, setAmi] = useState('60');

    usePageSeo({
        title: 'California Income & Rent Limits Lookup — LIHTC / AMI (2026)',
        description: 'Free California income and rent limits lookup for LIHTC housing — max income by household size and max rent by bedroom, by county and AMI level (2026 CTCAC).',
        keywords: 'California income limits 2026, AMI limits, LIHTC rent limits, max rent affordable housing, CTCAC income limits, county income limits, 60% AMI, 50% AMI, affordable housing rent California',
    });

    const data = limits[county];
    const amiLevels = useMemo(
        () => Object.keys(data.income || {}).sort((a, b) => Number(b) - Number(a)),
        [data]
    );
    const incomeRow = data.income?.[ami];
    const rentRow = data.rent?.[ami];

    return (
        <Fragment>
            <style dangerouslySetInnerHTML={{ __html: amiCss }} />
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={'California Income & Rent Limits 2026 (LIHTC / AMI)'} pagesub={'AMI Lookup'} pageTop={'Affordable Housing'} />
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="text-center mb-4" style={{ maxWidth: 760, margin: '0 auto' }}>
                            <h2 className="mb-3">Look up LIHTC income &amp; rent limits</h2>
                            <p className="m-0 text-muted">
                                Select a county and AMI level to see the maximum qualifying income by household size and the
                                maximum allowable rent by bedroom count — straight from the 2026 CTCAC limits.
                            </p>
                        </div>

                        {/* Selectors */}
                        <div className="row justify-content-center mb-4">
                            <div className="col-md-5 col-lg-4 mb-3">
                                <label className="input_title d-block mb-1">County</label>
                                <select className="ami__select" value={county} onChange={(e) => setCounty(e.target.value)} aria-label="Select county">
                                    {COUNTIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="col-md-5 col-lg-4 mb-3">
                                <label className="input_title d-block mb-1">AMI level</label>
                                <select className="ami__select" value={ami} onChange={(e) => setAmi(e.target.value)} aria-label="Select AMI level">
                                    {amiLevels.map((a) => <option key={a} value={a}>{a}% AMI</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="text-center mb-4">
                            <span style={{ background: 'var(--bs-primary-bg-subtle)', color: 'var(--bs-primary)', fontWeight: 700, borderRadius: 20, padding: '6px 18px' }}>
                                {data.name} County · {ami}% AMI
                            </span>
                        </div>

                        <div className="row">
                            {/* Income limits */}
                            <div className="col-lg-6 mb-4">
                                <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 16, padding: '24px 28px', height: '100%' }}>
                                    <h3 className="h5 mb-1">Maximum income</h3>
                                    <p className="small text-muted mb-3">Gross annual household income limit by household size.</p>
                                    <table className="table table-sm align-middle mb-0">
                                        <tbody>
                                            {HOUSEHOLD.map((h, i) => (
                                                <tr key={h}>
                                                    <td style={{ color: 'var(--bs-body-color)' }}>{h}</td>
                                                    <td className="text-end fw-semibold" style={{ color: 'var(--bs-dark)' }}>{fmt(incomeRow && incomeRow[i])}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Rent limits */}
                            <div className="col-lg-6 mb-4">
                                <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 16, padding: '24px 28px', height: '100%' }}>
                                    <h3 className="h5 mb-1">Maximum rent</h3>
                                    <p className="small text-muted mb-3">Max gross monthly rent (incl. utility allowance) by unit size.</p>
                                    <table className="table table-sm align-middle mb-0">
                                        <tbody>
                                            {BEDROOMS.map((b, i) => (
                                                <tr key={b}>
                                                    <td style={{ color: 'var(--bs-body-color)' }}>{b}</td>
                                                    <td className="text-end fw-semibold" style={{ color: 'var(--bs-dark)' }}>{fmt(rentRow && rentRow[i])}/mo</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="text-center mt-2">
                            <Link to="/contact" className="btn btn-primary">
                                <span className="btn_label" data-text="Plan your project">Building here? Plan your project</span>
                                <span className="btn_icon"><i className="fa-solid fa-arrow-up-right" aria-hidden="true"></i></span>
                            </Link>
                        </div>

                        <p className="small text-muted mt-4" style={{ maxWidth: 860 }}>
                            Source: California Tax Credit Allocation Committee (CTCAC) 2026 Income &amp; Rent Limits, effective
                            May 1, 2026 (for projects placed in service from 1/1/2009 onward; CTCAC's hold-harmless rules mean a
                            small number of counties may differ slightly depending on your project's exact placed-in-service
                            date). Rents shown are gross — the tenant-paid rent is reduced by the project's utility allowance.
                            Always confirm against the official CTCAC limits for your project's placed-in-service date.
                        </p>

                        <p className="small mt-3" style={{ maxWidth: 860 }}>
                            Use these limits in the <Link to="/feasibility-pro-forma">Affordable Housing Feasibility Pro Forma</Link>,
                            or check application dates on the <Link to="/ctcac-deadlines">CTCAC &amp; CDLAC deadline calendar</Link>.
                        </p>
                    </div>
                </section>
                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default AmiLimitsPage;
